/**
 * Deterministic pseudo-randomness, seeded per tile.
 *
 * Scattered detail — trees, grass, rocks — must land in exactly the same place
 * every time a tile is built. Tiles are evicted and rebuilt constantly as the
 * player moves, and a global or time-seeded generator would reshuffle a forest
 * every time you walked away and came back. Seeding from the tile key makes the
 * scatter a pure function of position.
 */

import type { TileKey } from '../geo/tilekey.js';

/** mulberry32: small, fast, and good enough for scattering vegetation. */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * A seed derived from a tile key, mixed so that neighbouring tiles — whose
 * keys differ by one — do not produce visibly correlated patterns.
 */
export function seedFromTile(key: TileKey, salt = 0): number {
  let h = 2166136261 >>> 0;
  for (const value of [key.z, key.x, key.y, salt]) {
    h ^= value & 0xffff;
    h = Math.imul(h, 16777619) >>> 0;
    h ^= (value >>> 16) & 0xffff;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}
